import { Theme } from "@material-ui/core/styles";
import { createStyles, makeStyles } from "@material-ui/styles";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
} from "@material-ui/core";
import React, { useState } from "react";
import { PaymentMethodType } from "./PaymentConstants";
import TranslatedMessage from "../translations/TranslatedMessage";
import { useGetMeQuery, useUpdateMeMutation } from "../../api/rtk/MeApi";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import PaymentMethodItem from "./PaymentMethodItem";
import InvoicePaymentForm from "./InvoicePaymentForm";
import { CandidateViewModel } from "../../model/CandidateViewModel";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttons: {
      padding: theme.spacing(1),
      display: "flex",
      justifyContent: "center",
    },
    content: {
      padding: theme.spacing(1),
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: theme.spacing(2) + "px",
    },
    selector: {
      padding: theme.spacing(1),
      display: "flex",
      justifyContent: "center",
      gap: theme.spacing(1) + "px",
    },
    form: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: theme.spacing(1) + "px",
    },
    icon: {
      height: theme.spacing(2),
    },
    singleIcon: {
      height: theme.spacing(4),
    },
  })
);

type PaymentMethodSelectorDialogProps = {
  onClose: () => void;
  onPay: (paymentMethod: PaymentMethodType) => void;
};

const PaymentMethodSelectorDialog: React.FC<PaymentMethodSelectorDialogProps> = ({
  onClose,
  onPay,
}: PaymentMethodSelectorDialogProps) => {
  const classes = useStyles();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType | undefined>();
  const { data: me, isFetching: isMeFetching } = useGetMeQuery();
  const [updateMe, { isLoading: isSavingMe }] = useUpdateMeMutation();
  const handleSelect = (type: PaymentMethodType) => {
    setSelectedMethod(type);
    if (type === PaymentMethodType.CreditCard) {
      onPay(type);
    } else if (type === PaymentMethodType.CembraPay && me?.birthdate && me?.phoneNumber) {
      onPay(type);
    } else {
      setSelectedMethod(type);
    }
  };

  const handleInvoicePaymentSubmit = async (value: CandidateViewModel) => {
    if (me!.phoneNumber !== value.phoneNumber || me!.birthdate !== value.birthdate) {
      await updateMe(value);
    }
    onPay(PaymentMethodType.CembraPay);
  };

  if (isMeFetching || isSavingMe) {
    return <LinearProgress variant="query" />;
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>
        <TranslatedMessage id="paymentMethodSelector.title" />
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Box className={classes.selector}>
          <PaymentMethodItem
            selected={selectedMethod}
            type={PaymentMethodType.CreditCard}
            icon={<CreditCardIcon />}
            onSelect={handleSelect}
          ></PaymentMethodItem>
          <PaymentMethodItem
            selected={selectedMethod}
            type={PaymentMethodType.CembraPay}
            icon={<img className={classes.singleIcon} src="/images/icons/cembraPay.png" alt="CembraPay" />}
            onSelect={handleSelect}
          ></PaymentMethodItem>
        </Box>
        {selectedMethod === PaymentMethodType.CembraPay && me && (
          <Paper className={classes.form}>
            <InvoicePaymentForm me={me} onSubmit={handleInvoicePaymentSubmit}></InvoicePaymentForm>
          </Paper>
        )}
      </DialogContent>
      <DialogActions className={classes.buttons}>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentMethodSelectorDialog;
